/**
 * Onboarding — Pre-auth guided onboarding flow.
 * Flow: Welcome → Role Selection → 3 role-specific slides → Register CTA
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, ArrowLeft, Clock, FileText, Users, Crown, Baby, Home, CheckCircle, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

// Role-specific onboarding slides
const PARENT_SLIDES = [
  {
    icon: Clock,
    title: 'Suivez facilement les heures de garde',
    text: 'Notez et validez les heures avec votre nounou en toute simplicité.',
    accent: 'var(--secondary-400)',
  },
  {
    icon: FileText,
    title: 'Simplifiez votre administratif',
    text: 'Générez vos déclarations et documents sans effort.',
    accent: 'var(--secondary-400)',
  },
  {
    icon: Users,
    title: 'Un seul abonnement suffit',
    text: 'Si vous ou votre nounou avez un abonnement, vous profitez tous les deux des fonctionnalités complètes.',
    accent: 'var(--primary-500)',
    highlight: true,
  },
];

const NANNY_SLIDES = [
  {
    icon: Home,
    title: 'Gérez toutes vos familles au même endroit',
    text: 'Centralisez vos heures et votre planning facilement.',
    accent: 'var(--primary-500)',
  },
  {
    icon: Clock,
    title: 'Gagnez du temps au quotidien',
    text: 'Suivez vos heures et vos revenus sans effort.',
    accent: 'var(--primary-500)',
  },
  {
    icon: Crown,
    title: 'Un seul abonnement pour tout gérer',
    text: 'Avec un abonnement, toutes vos familles bénéficient des fonctionnalités complètes.',
    accent: 'var(--secondary-400)',
    highlight: true,
  },
];

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('welcome'); // 'welcome' | 'role' | 'slides'
  const [selectedRole, setSelectedRole] = useState(null);
  const [slideIndex, setSlideIndex] = useState(0);

  const slides = selectedRole === 'parent' ? PARENT_SLIDES : NANNY_SLIDES;
  const isLastSlide = slideIndex >= slides.length - 1;

  const goToRegister = () => {
    navigate(`/register?role=${selectedRole}`);
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setSlideIndex(0);
    setStep('slides');
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-8"
      style={{ background: 'linear-gradient(145deg, var(--bg-tertiary) 0%, var(--bg-secondary) 50%, var(--bg-primary) 100%)' }}
      data-testid="onboarding-page"
    >
      {/* Logo */}
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'var(--font-secondary)', color: 'var(--primary-600)' }}>
          Nannyou
        </h1>
      </div>

      {/* ═══ WELCOME ═══ */}
      {step === 'welcome' && (
        <Card className="w-full max-w-md shadow-xl border-0" data-testid="onboarding-welcome">
          <CardContent className="pt-10 pb-8 px-8 text-center space-y-6">
            {/* Illustration */}
            <div className="relative mx-auto w-32 h-32 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--primary-100)' }}>
              <img
                src="https://images.pexels.com/photos/1116050/pexels-photo-1116050.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=200"
                alt="Famille heureuse"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="space-y-3">
              <h2 className="text-2xl font-bold leading-tight" style={{ fontFamily: 'var(--font-secondary)', color: 'var(--text-primary)' }}>
                Gérez simplement la garde de vos enfants
              </h2>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                Suivi des heures, déclarations et organisation — tout au même endroit.
              </p>
            </div>

            <Button
              onClick={() => setStep('role')}
              className="w-full h-12 text-base font-semibold gap-2 text-white"
              style={{ backgroundColor: 'var(--primary-500)' }}
              data-testid="start-btn"
            >
              Commencer <ArrowRight className="h-4 w-4" />
            </Button>

            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Déjà un compte ?{' '}
              <a href="/login" className="underline hover:no-underline" style={{ color: 'var(--primary-400)' }}>Se connecter</a>
            </p>
          </CardContent>
        </Card>
      )}

      {/* ═══ ROLE SELECTION ═══ */}
      {step === 'role' && (
        <Card className="w-full max-w-md shadow-xl border-0" data-testid="onboarding-role">
          <CardContent className="pt-10 pb-8 px-8 space-y-6">
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-secondary)', color: 'var(--text-primary)' }}>
                Vous êtes…
              </h2>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Choisissez votre profil pour personnaliser votre expérience.</p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <button
                onClick={() => handleRoleSelect('parent')}
                className={cn(
                  "group relative p-6 rounded-xl border-2 text-left transition-all duration-200",
                  "hover:shadow-lg hover:scale-[1.02] focus-visible:ring-2 focus-visible:ring-offset-2",
                  "border-[var(--neutral-200)] hover:border-[var(--secondary-400)]"
                )}
                style={{ backgroundColor: 'var(--bg-primary)' }}
                data-testid="role-parent-btn"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: 'var(--secondary-100)' }}>
                    <Baby className="h-7 w-7" style={{ color: 'var(--secondary-500)' }} />
                  </div>
                  <div>
                    <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Parent</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>Je fais garder mes enfants</p>
                  </div>
                  <ArrowRight className="h-5 w-5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--secondary-400)' }} />
                </div>
              </button>

              <button
                onClick={() => handleRoleSelect('nanny')}
                className={cn(
                  "group relative p-6 rounded-xl border-2 text-left transition-all duration-200",
                  "hover:shadow-lg hover:scale-[1.02] focus-visible:ring-2 focus-visible:ring-offset-2",
                  "border-[var(--neutral-200)] hover:border-[var(--primary-500)]"
                )}
                style={{ backgroundColor: 'var(--bg-primary)' }}
                data-testid="role-nanny-btn"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: 'var(--primary-100)' }}>
                    <Crown className="h-7 w-7" style={{ color: 'var(--primary-500)' }} />
                  </div>
                  <div>
                    <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Nounou</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>Je suis assistante maternelle</p>
                  </div>
                  <ArrowRight className="h-5 w-5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--primary-500)' }} />
                </div>
              </button>
            </div>

            <button
              onClick={() => setStep('welcome')}
              className="w-full text-center text-xs underline hover:no-underline py-1"
              style={{ color: 'var(--text-muted)' }}
            >
              ← Retour
            </button>
          </CardContent>
        </Card>
      )}

      {/* ═══ ROLE-SPECIFIC SLIDES ═══ */}
      {step === 'slides' && (
        <Card className="w-full max-w-md shadow-xl border-0" data-testid="onboarding-slides">
          <CardContent className="pt-8 pb-8 px-8 space-y-6">
            {/* Dots */}
            <div className="flex items-center justify-center gap-2">
              {slides.map((_, i) => (
                <div
                  key={i}
                  className={cn("rounded-full transition-all duration-300", i === slideIndex ? "w-8 h-2" : "w-2 h-2")}
                  style={{ backgroundColor: i === slideIndex ? slides[slideIndex].accent : 'var(--neutral-200)' }}
                />
              ))}
            </div>

            {/* Slide content */}
            <div className="text-center space-y-4 min-h-[200px] flex flex-col items-center justify-center">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto"
                style={{ backgroundColor: `color-mix(in srgb, ${slides[slideIndex].accent} 12%, transparent)` }}
              >
                {(() => {
                  const IconComponent = slides[slideIndex].icon;
                  return <IconComponent className="h-8 w-8" style={{ color: slides[slideIndex].accent }} />;
                })()}
              </div>

              <h3 className="text-xl font-bold leading-tight" style={{ fontFamily: 'var(--font-secondary)', color: 'var(--text-primary)' }}>
                {slides[slideIndex].title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                {slides[slideIndex].text}
              </p>

              {slides[slideIndex].highlight && (
                <Badge className="text-[11px] px-3 py-1 bg-[var(--primary-50)] text-[var(--primary-600)] border-[var(--primary-200)]">
                  <Sparkles className="h-3 w-3 mr-1" /> Point clé
                </Badge>
              )}
            </div>

            {/* Navigation buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  if (slideIndex === 0) {
                    setStep('role');
                  } else {
                    setSlideIndex(prev => prev - 1);
                  }
                }}
                className="gap-1.5"
                data-testid="onboarding-prev"
              >
                <ArrowLeft className="h-4 w-4" /> Retour
              </Button>

              <Button
                onClick={() => {
                  if (isLastSlide) {
                    goToRegister();
                  } else {
                    setSlideIndex(prev => prev + 1);
                  }
                }}
                className="flex-1 h-11 text-base font-semibold gap-2 text-white"
                style={{ backgroundColor: slides[slideIndex].accent }}
                data-testid="onboarding-next"
              >
                {isLastSlide ? (
                  <>Créer mon compte <ArrowRight className="h-4 w-4" /></>
                ) : (
                  <>Suivant <ArrowRight className="h-4 w-4" /></>
                )}
              </Button>
            </div>

            <p className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
              Déjà un compte ?{' '}
              <a href="/login" className="underline hover:no-underline" style={{ color: 'var(--primary-400)' }}>Se connecter</a>
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Onboarding;
